import React from 'react';
import PropTypes from 'prop-types';

import { PowerFunnelSurfaces } from '../../Analytics/constants';
import useFableticsCta from '../../hooks/useFableticsCta';
import { USER_STATUS_FREE } from '../../../utils/constants';
import { IMAGES, COPY } from '../../Fabletics/constants';
import FableticsPastDueMessage from './FableticsPastDueMessage';
import useAppleSubscriptionPopup from './useAppleSubscriptionPopup';
import styles from './fableticsVersionOne.scss';

// `freeHeadline` is unique to this variant; the monthly + annual headlines are
// shared with the FableticsSubscriptionsBanner and live in the Fabletics COPY
// constants module.
const FREE_HEADLINE = 'POWER: The Ultimate Upgrade for Your Game, Gear, and Getaways.';

const PowerCard = () => (
    <div className={styles.card} data-testid="fabletics.banner.version_one.power-card">
        <picture className={styles.powerHero}>
            <source media="(max-width: 1024px)" srcSet={IMAGES.powerHeroMobile} />
            <img src={IMAGES.powerHeroDesktop} alt="" className={styles.powerHeroImg} />
        </picture>
        <div className={styles.cardBody}>
            <div className={styles.brandRow} data-testid="fabletics.banner.version_one.power-card.wordmark">
                <img src={IMAGES.powerIcon} alt="" className={styles.brandIcon} />
                <span className={styles.brandLabelPower}>POWER</span>
            </div>
            <p className={styles.cardCopy}>
                {COPY.powerCard}
            </p>
        </div>
    </div>
);

const FableticsCard = () => (
    <div className={styles.card} data-testid="fabletics.banner.version_one.fabletics-card">
        <div className={styles.cardImageWrap}>
            <img src={IMAGES.tennisWomen} alt="" className={`${styles.cardImage} ${styles.cardImageFabletics}`} />
        </div>
        <div className={styles.cardBody}>
            <img
                src={IMAGES.fableticsLogo}
                alt="Fabletics"
                className={`${styles.brandLogo} ${styles.brandLogoFabletics}`}
                data-testid="fabletics.banner.version_one.fabletics-card.logo"
            />
            <p className={`${styles.cardCopy} ${styles.cardCopyFabletics}`}>
                {COPY.fableticsCard}
            </p>
        </div>
    </div>
);

const GpCard = () => (
    <div className={styles.card} data-testid="fabletics.banner.version_one.gp-card">
        <div className={styles.cardImageWrap}>
            <img src={IMAGES.airport} alt="" className={`${styles.cardImage} ${styles.cardImageGp}`} />
        </div>
        <div className={styles.cardBody}>
            <img
                src={IMAGES.gpLogo}
                alt="Global Privileges"
                className={`${styles.brandLogo} ${styles.brandLogoGp}`}
                data-testid="fabletics.banner.version_one.gp-card.logo"
            />
            <p className={styles.cardCopy}>
                {COPY.gpCard}
            </p>
        </div>
    </div>
);

const FableticsVersionOne = ({ surface, variant }) => {
    const { open: openAppleSubscriptionPopup, popup: appleSubscriptionPopup } = useAppleSubscriptionPopup();
    const cta = useFableticsCta({
        surface,
        variant,
        trackImpressions: true,
        onAppleManageClick: openAppleSubscriptionPopup,
    });
    const {
        buttonText,
        handleCtaClick,
        handleLearnMoreClick,
        hasPastDueSubscription,
        userStatus,
    } = cta;

    // Annual subscribers are routed to the control banner via useFabletics, so
    // this variant only ever renders for Free / Monthly / tier-null powered
    // users (the experiment audience).
    const isFree = userStatus === USER_STATUS_FREE;
    const headline = isFree ? FREE_HEADLINE : COPY.monthlyHeadline;

    return (
        <div
            className={styles.banner}
            data-testid="fabletics.banner.version_one.container"
        >
            <p
                className={styles.headline}
                data-testid="fabletics.banner.version_one.headline"
            >
                {headline}
            </p>
            {/* Free desktop: PowerCard, FableticsCard, and GpCard all live in
                the same row. Free mobile: cardRow becomes a flex column, so the
                "Included with the Annual Plan" subhead is rendered inline
                between the Power card and the Fabletics/GP pair (hidden on
                desktop). The ZIP caption follows the same inline pattern for
                monthly mobile only. */}
            <div className={styles.cardRow}>
                {isFree && <PowerCard />}
                {isFree && (
                    <p
                        className={styles.includedSubhead}
                        data-testid="fabletics.banner.version_one.included-subhead"
                    >
                        Included with the Annual Plan
                    </p>
                )}
                <FableticsCard />
                <GpCard />
            </div>
            <div className={styles.ctaSection}>
                <FableticsPastDueMessage
                    hasPastDueSubscription={hasPastDueSubscription}
                    className={styles.pastDueMessage}
                    testId="fabletics.banner.version_one.past-due-message"
                />
                <button
                    type="button"
                    className={styles.primaryCta}
                    data-testid="fabletics.banner.version_one.primary-cta"
                    onClick={handleCtaClick}
                >
                    {buttonText}
                </button>
                <button
                    type="button"
                    className={styles.learnMore}
                    data-testid="fabletics.banner.version_one.learn-more"
                    onClick={handleLearnMoreClick}
                >
                    Learn more
                </button>
            </div>
            {appleSubscriptionPopup}
        </div>
    );
};

FableticsVersionOne.propTypes = {
    surface: PropTypes.oneOf([PowerFunnelSurfaces.HOME, PowerFunnelSurfaces.POWER_PERKS_PAGE]),
    variant: PropTypes.string,
};

FableticsVersionOne.defaultProps = {
    surface: PowerFunnelSurfaces.HOME,
    variant: 'version_one',
};

export default FableticsVersionOne;
